import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download, LogOut, RefreshCw, ExternalLink, Eye, Users, CheckCircle, Clock, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MU_LOGO_URL = 'https://customer-assets.emergentagent.com/job_career-heat-map/artifacts/8zniuor4_MU%20Logo.png';

const getToken = () => localStorage.getItem('mu_admin_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });
const resumeViewUrl = id => `${BACKEND_URL}/api/admin/original-file/${id}?token=${getToken()}`;
const reportViewUrl = id => id ? `${window.location.origin}/?report=${id}` : '';

const StatusBadge = ({ status }) => {
  const c = {
    completed: { bg: 'rgba(63,168,106,0.10)', border: 'rgba(63,168,106,0.25)', color: '#2A8050', label: 'Completed' },
    analyzing: { bg: 'rgba(232,160,32,0.10)', border: 'rgba(232,160,32,0.25)', color: '#9A6200', label: 'Analyzing' },
    failed:    { bg: 'rgba(217,64,64,0.08)', border: 'rgba(217,64,64,0.20)', color: '#C03030', label: 'Failed' },
    uploaded:  { bg: 'rgba(0,0,0,0.04)', border: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.50)', label: 'Uploaded' },
  }[status] || { bg: 'rgba(0,0,0,0.04)', border: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.50)', label: status };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 9px', borderRadius:'9999px', backgroundColor:c.bg, border:`1px solid ${c.border}`, fontFamily:'DM Sans,sans-serif', fontSize:'11px', fontWeight:600, color:c.color }}>
      {c.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div style={{ backgroundColor:'#ffffff', border:'1px solid rgba(0,0,0,0.08)', borderRadius:'12px', padding:'16px 20px', display:'flex', alignItems:'center', gap:'14px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', flex:'1 1 140px' }}>
    <div style={{ width:'40px', height:'40px', borderRadius:'10px', flexShrink:0, backgroundColor:`${color}14`, border:`1px solid ${color}30`, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Icon size={18} color={color} />
    </div>
    <div>
      <p style={{ fontFamily:'DM Sans', fontSize:'22px', fontWeight:700, color:'#111111', lineHeight:1 }}>{value ?? '—'}</p>
      <p style={{ fontFamily:'DM Sans', fontSize:'12px', color:'rgba(0,0,0,0.45)', marginTop:'2px' }}>{label}</p>
    </div>
  </div>
);

export const AdminDashboard = () => {
  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [pages,       setPages]       = useState(1);
  const LIMIT = 100;

  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting,    setDeleting]    = useState(false);
  const [exporting,   setExporting]   = useState(false);

  const token = getToken();
  useEffect(() => { if (!token) window.location.href = '/admin'; }, [token]);

  const fetchData = useCallback(async (pg = page) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to',   dateTo);

      const [subRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/submissions?${params}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/stats`,                { headers: authHeaders() }),
      ]);

      const data = subRes.data;
      // Handle both new { items, total, pages } shape and old flat array
      if (Array.isArray(data)) {
        setItems(data); setTotal(data.length); setPages(1);
      } else {
        setItems(data.items || []); setTotal(data.total || 0); setPages(data.pages || 1);
      }
      setPage(pg);
      setStats(statsRes.data);
      setSelectedIds(new Set());
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('mu_admin_token'); window.location.href = '/admin'; }
      else setError('Failed to load data. Please try again.');
    } finally { setLoading(false); }
  }, [page, dateFrom, dateTo]);

  useEffect(() => { fetchData(1); }, []); // eslint-disable-line

  const handleFilter = () => fetchData(1);
  const handleClearFilter = () => { setDateFrom(''); setDateTo(''); setTimeout(() => fetchData(1), 0); };

  const handleLogout = () => { localStorage.removeItem('mu_admin_token'); window.location.href = '/admin'; };

  const filtered = items.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name||'').toLowerCase().includes(q) || (s.email||'').toLowerCase().includes(q) || (s.phone||'').includes(q);
  });

  const allSelected  = filtered.length > 0 && filtered.every(s => selectedIds.has(s.id));
  const someSelected = filtered.some(s => selectedIds.has(s.id));
  const toggleAll    = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach(s => next.delete(s.id));
      else             filtered.forEach(s => next.add(s.id));
      return next;
    });
  };
  const toggleOne = id => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleBulkDelete = async () => {
    if (!selectedIds.size || !window.confirm(`Delete ${selectedIds.size} entr${selectedIds.size===1?'y':'ies'}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/submissions`, { headers: authHeaders(), data: { ids: Array.from(selectedIds) } });
      await fetchData(1);
    } catch { alert('Delete failed. Please try again.'); }
    finally { setDeleting(false); }
  };

  const generateCSVFrontend = () => {
    const headers = ['Name','Email','Phone','Current CTC','Resume','Report','Status','Date'];
    const esc = v => `"${String(v||'').replace(/"/g,'""')}"`;
    const rows = items.map(s => [
      esc(s.name), esc(s.email), esc(s.phone), esc(s.current_ctc),
      esc(s.id ? resumeViewUrl(s.id) : ''),
      esc(s.analysis_id ? reportViewUrl(s.analysis_id) : ''),
      esc(s.status),
      esc(s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : ''),
    ].join(','));
    const csv = [headers.map(h=>esc(h)).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    const suffix = (dateFrom || dateTo) ? `_${dateFrom||'start'}_to_${dateTo||'today'}` : '';
    a.download = `mu_submissions${suffix}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const thStyle = { padding:'10px 14px', fontFamily:'DM Sans,sans-serif', fontSize:'11px', fontWeight:700, color:'rgba(0,0,0,0.45)', textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:'1px solid rgba(0,0,0,0.08)', backgroundColor:'#fafafa', whiteSpace:'nowrap', textAlign:'left' };
  const tdStyle = { padding:'11px 14px', fontFamily:'DM Sans,sans-serif', fontSize:'13px', color:'#111111', borderBottom:'1px solid rgba(0,0,0,0.06)', verticalAlign:'middle' };
  const inputStyle = { fontFamily:'DM Sans,sans-serif', fontSize:'13px', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'8px', padding:'7px 10px', color:'#111', backgroundColor:'#ffffff', outline:'none' };

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'#f5f5f5', fontFamily:'DM Sans,sans-serif' }}>
      {/* Header */}
      <div style={{ backgroundColor:'#ffffff', borderBottom:'1px solid rgba(0,0,0,0.08)', padding:'0 24px', height:'60px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:30, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <img src={MU_LOGO_URL} alt="Masters' Union" style={{ height:'24px', objectFit:'contain' }} />
          <span style={{ backgroundColor:'rgba(232,160,32,0.12)', border:'1px solid rgba(232,160,32,0.25)', borderRadius:'9999px', padding:'2px 10px', fontSize:'11px', fontWeight:700, color:'#9A6200', letterSpacing:'0.06em' }}>ADMIN</span>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          {someSelected && (
            <button onClick={handleBulkDelete} disabled={deleting} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', backgroundColor:deleting?'#f5f5f5':'rgba(217,64,64,0.08)', border:'1px solid rgba(217,64,64,0.25)', borderRadius:'9999px', cursor:deleting?'not-allowed':'pointer', fontSize:'12px', fontWeight:600, color:deleting?'rgba(0,0,0,0.40)':'#C03030' }}>
              <Trash2 size={13} /> Delete {selectedIds.size}
            </button>
          )}
          <button onClick={() => fetchData(page)} disabled={loading} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 12px', backgroundColor:'#f5f5f5', border:'1px solid rgba(0,0,0,0.10)', borderRadius:'9999px', cursor:loading?'not-allowed':'pointer', fontSize:'12px', fontWeight:500, color:'#111' }}>
            <RefreshCw size={13} style={{ animation:loading?'spin 1s linear infinite':'none' }} /> Refresh
          </button>
          <button onClick={generateCSVFrontend} disabled={items.length===0} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', backgroundColor:'#E8A020', border:'none', borderRadius:'9999px', cursor:items.length===0?'not-allowed':'pointer', fontSize:'12px', fontWeight:700, color:'#ffffff', opacity:items.length===0?0.6:1 }}>
            <Download size={13} /> Export CSV{(dateFrom||dateTo)?' (filtered)':''}
          </button>
          <button onClick={handleLogout} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 12px', backgroundColor:'transparent', border:'1px solid rgba(0,0,0,0.10)', borderRadius:'9999px', cursor:'pointer', fontSize:'12px', color:'rgba(0,0,0,0.50)' }}>
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      <div style={{ maxWidth:'1380px', margin:'0 auto', padding:'20px' }}>
        {/* Page heading */}
        <div style={{ marginBottom:'16px' }}>
          <h1 style={{ fontSize:'22px', fontWeight:700, color:'#111111', marginBottom:'2px' }}>Submissions</h1>
          <p style={{ fontSize:'13px', color:'rgba(0,0,0,0.44)' }}>All career analysis requests</p>
        </div>

        {/* Stats */}
        {stats && (
          <div style={{ display:'flex', gap:'12px', flexWrap:'wrap', marginBottom:'16px' }}>
            <StatCard icon={Users}        label="Total"     value={stats.total}     color="#111111" />
            <StatCard icon={CheckCircle}  label="Completed" value={stats.completed} color="#3FA86A" />
            <StatCard icon={Clock}        label="Analyzing" value={stats.analyzing} color="#E8A020" />
          </div>
        )}

        {/* ── FILTERS row ── */}
        <div style={{ backgroundColor:'#ffffff', border:'1px solid rgba(0,0,0,0.08)', borderRadius:'12px', padding:'14px 16px', marginBottom:'14px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
          <span style={{ fontFamily:'DM Sans', fontSize:'12px', fontWeight:600, color:'rgba(0,0,0,0.45)', flexShrink:0 }}>FILTER BY DATE</span>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              <label style={{ fontSize:'12px', color:'rgba(0,0,0,0.50)', whiteSpace:'nowrap' }}>From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inputStyle, fontSize:'12px' }} />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              <label style={{ fontSize:'12px', color:'rgba(0,0,0,0.50)', whiteSpace:'nowrap' }}>To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...inputStyle, fontSize:'12px' }} />
            </div>
            <button onClick={handleFilter} style={{ padding:'7px 16px', backgroundColor:'#E8A020', border:'none', borderRadius:'9999px', cursor:'pointer', fontSize:'12px', fontWeight:700, color:'#fff' }}>
              Apply
            </button>
            {(dateFrom || dateTo) && (
              <button onClick={handleClearFilter} style={{ padding:'7px 14px', backgroundColor:'transparent', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'9999px', cursor:'pointer', fontSize:'12px', color:'rgba(0,0,0,0.50)' }}>
                Clear
              </button>
            )}
          </div>

          {/* Search */}
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone..." style={{ ...inputStyle, minWidth:'240px' }}
            onFocus={e => e.target.style.borderColor = '#E8A020'}
            onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.12)'}
          />
        </div>

        {error && (
          <div style={{ backgroundColor:'rgba(217,64,64,0.06)', border:'1px solid rgba(217,64,64,0.20)', borderRadius:'10px', padding:'12px 16px', marginBottom:'12px', fontSize:'14px', color:'#D94040' }}>{error}</div>
        )}

        {/* Table */}
        <div style={{ backgroundColor:'#ffffff', border:'1px solid rgba(0,0,0,0.08)', borderRadius:'12px', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          {loading ? (
            <div style={{ padding:'48px', textAlign:'center', color:'rgba(0,0,0,0.40)', fontSize:'14px' }}>Loading submissions...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center', color:'rgba(0,0,0,0.40)', fontSize:'14px' }}>{search ? 'No results match your search.' : 'No submissions found.'}</div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'800px' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width:'40px', paddingRight:0 }}>
                      <div onClick={toggleAll} style={{ width:'16px', height:'16px', borderRadius:'4px', cursor:'pointer', backgroundColor:allSelected?'#E8A020':'#ffffff', border:`1.5px solid ${allSelected?'#E8A020':'rgba(0,0,0,0.20)'}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}>
                        {allSelected && <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        {someSelected && !allSelected && <div style={{ width:'8px', height:'1.5px', backgroundColor:'#E8A020' }} />}
                      </div>
                    </th>
                    {['Name','Email','Phone','Current CTC','Resume','Report','Status','Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => {
                    const isSel = selectedIds.has(s.id);
                    return (
                      <tr key={s.id} onClick={() => toggleOne(s.id)} style={{ backgroundColor:isSel?'rgba(232,160,32,0.04)':i%2===0?'#ffffff':'#fafafa', cursor:'pointer' }}>
                        <td style={{ ...tdStyle, width:'40px', paddingRight:0 }}>
                          <div style={{ width:'16px', height:'16px', borderRadius:'4px', backgroundColor:isSel?'#E8A020':'#ffffff', border:`1.5px solid ${isSel?'#E8A020':'rgba(0,0,0,0.18)'}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}>
                            {isSel && <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                        </td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}><span style={{ fontWeight:600 }}>{s.name||'—'}</span></td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}>{s.email||'—'}</td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}>{s.phone||'—'}</td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}>{s.current_ctc?<span style={{ fontWeight:500, color:'#9A6200' }}>{s.current_ctc}</span>:<span style={{ color:'rgba(0,0,0,0.30)' }}>—</span>}</td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}>
                          {s.id ? <a href={resumeViewUrl(s.id)} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:'4px', color:'#E8A020', fontSize:'12px', fontWeight:600, textDecoration:'none', padding:'3px 8px', backgroundColor:'rgba(232,160,32,0.08)', border:'1px solid rgba(232,160,32,0.20)', borderRadius:'9999px' }}><Eye size={11} /> View</a> : '—'}
                        </td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}>
                          {s.analysis_id ? <a href={reportViewUrl(s.analysis_id)} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:'4px', color:'#3FA86A', fontSize:'12px', fontWeight:600, textDecoration:'none', padding:'3px 8px', backgroundColor:'rgba(63,168,106,0.08)', border:'1px solid rgba(63,168,106,0.20)', borderRadius:'9999px' }}><ExternalLink size={11} /> Open</a> : '—'}
                        </td>
                        <td style={tdStyle} onClick={e=>e.stopPropagation()}><StatusBadge status={s.status} /></td>
                        <td style={{ ...tdStyle, color:'rgba(0,0,0,0.45)', fontSize:'12px', whiteSpace:'nowrap' }} onClick={e=>e.stopPropagation()}>
                          {s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── PAGINATION row ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'12px', flexWrap:'wrap', gap:'8px' }}>
          <p style={{ fontSize:'13px', color:'rgba(0,0,0,0.50)' }}>
            Showing <strong>{((page-1)*LIMIT)+1}–{Math.min(page*LIMIT, total)}</strong> of <strong>{total}</strong> submissions
            {(dateFrom||dateTo) && <span style={{ color:'#E8A020', marginLeft:'6px' }}>(filtered)</span>}
          </p>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <button onClick={() => fetchData(page-1)} disabled={page<=1||loading} style={{ display:'flex', alignItems:'center', gap:'4px', padding:'7px 14px', backgroundColor:'#ffffff', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'9999px', cursor:page<=1?'not-allowed':'pointer', fontSize:'13px', fontWeight:500, color:page<=1?'rgba(0,0,0,0.30)':'#111', opacity:page<=1?0.5:1 }}>
              <ChevronLeft size={14} /> Previous
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              {Array.from({length: Math.min(pages, 7)}, (_, i) => {
                const pg = pages <= 7 ? i+1 : (page <= 4 ? i+1 : page - 3 + i);
                if (pg < 1 || pg > pages) return null;
                return (
                  <button key={pg} onClick={() => fetchData(pg)} style={{ width:'32px', height:'32px', borderRadius:'8px', border:'none', cursor:'pointer', fontFamily:'DM Sans', fontSize:'13px', fontWeight: pg===page ? 700 : 400, backgroundColor: pg===page ? '#E8A020' : 'transparent', color: pg===page ? '#fff' : 'rgba(0,0,0,0.60)' }}>
                    {pg}
                  </button>
                );
              })}
            </div>
            <button onClick={() => fetchData(page+1)} disabled={page>=pages||loading} style={{ display:'flex', alignItems:'center', gap:'4px', padding:'7px 14px', backgroundColor:'#ffffff', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'9999px', cursor:page>=pages?'not-allowed':'pointer', fontSize:'13px', fontWeight:500, color:page>=pages?'rgba(0,0,0,0.30)':'#111', opacity:page>=pages?0.5:1 }}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
